/**
 * Propriedade de VM (#999) — cliente HTTP do portal do Cloud Provider.
 *
 * Adoção: VM que existe no Proxmox sem registro no sistema ganha um par
 * (revenda, cliente final). Transferência: VM registrada muda de par. As duas
 * têm prévia (`preview`) que diz o que será criado, cobrado, movido e mantido —
 * antes de confirmar, não depois. Tipos e frases da prévia: `utils/vmOwnership.ts`.
 */

import type {
  AdoptionRequest,
  OwnershipDestinations,
  OwnershipPreview,
  OwnershipResult,
  OwnershipTransferRecord,
  ResellerTransferDestinations,
  ResellerTransferRequest,
  TransferRequest,
} from '../utils/vmOwnership'
import { apiClient } from './api'

export type {
  AdoptionRequest,
  OwnershipCustomer,
  OwnershipDestinations,
  OwnershipNetworkMode,
  OwnershipPreview,
  OwnershipProduct,
  OwnershipReseller,
  OwnershipResult,
  OwnershipTransferMarker,
  OwnershipTransferRecord,
  OwnershipVpc,
  ResellerTransferDestinations,
  ResellerTransferRequest,
  TransferRequest,
} from '../utils/vmOwnership'

// `apiClient` já devolve `response.data`; o prefixo `/api/v1` é o mesmo dos demais services.
const base = '/api/v1/vm-ownership'

export const vmOwnershipApi = {
  getDestinations: (clusterId: string): Promise<OwnershipDestinations> =>
    apiClient.get<OwnershipDestinations>(`${base}/clusters/${clusterId}/destinations`),

  // As prévias fazem a MESMA varredura do cluster que a execução (sem cache): 30 s
  // não bastam num cluster grande com o agent enfileirado, e sem prévia o botão
  // de confirmar nem habilita.
  previewAdoption: (
    clusterId: string,
    vmid: number,
    body: AdoptionRequest,
  ): Promise<OwnershipPreview> =>
    apiClient.postLong<OwnershipPreview>(
      `${base}/clusters/${clusterId}/vms/${vmid}/adoption/preview`,
      body,
    ),

  adopt: (clusterId: string, vmid: number, body: AdoptionRequest): Promise<OwnershipResult> =>
    apiClient.postLong<OwnershipResult>(`${base}/clusters/${clusterId}/vms/${vmid}/adoption`, body),

  previewTransfer: (vmInstanceId: string, body: TransferRequest): Promise<OwnershipPreview> =>
    apiClient.postLong<OwnershipPreview>(
      `${base}/instances/${vmInstanceId}/transfer/preview`,
      body,
    ),

  // `postLong`: a operação fala com o Proxmox (varredura do cluster + PUT + releitura)
  // e o timeout padrão de 30s a cortaria pela metade — com a tentativa em andamento.
  transfer: (vmInstanceId: string, body: TransferRequest): Promise<OwnershipResult> =>
    apiClient.postLong<OwnershipResult>(`${base}/instances/${vmInstanceId}/transfer`, body),

  listTransfers: (
    clusterId: string,
    vmid?: number,
  ): Promise<{ transfers: OwnershipTransferRecord[] }> =>
    apiClient.get<{ transfers: OwnershipTransferRecord[] }>(
      `${base}/clusters/${clusterId}/transfers`,
      {
        params: vmid !== undefined ? { vmid } : undefined,
      },
    ),
}

/**
 * Transferência PELA REVENDA (#1000): a VM é dela e só o cliente muda. Mesma prévia
 * e mesmo resultado do motor da #999; a revenda de destino é a org ativa, por
 * construção (o corpo nem a carrega).
 */
const baseRevenda = '/api/v1/reseller/instances'

export const resellerVmOwnershipApi = {
  getDestinations: (instanceId: string): Promise<ResellerTransferDestinations> =>
    apiClient.get<ResellerTransferDestinations>(
      `${baseRevenda}/${instanceId}/transfer/destinations`,
    ),

  // `postLong` pelos mesmos motivos do CP: a prévia e a execução varrem o cluster.
  previewTransfer: (instanceId: string, body: ResellerTransferRequest): Promise<OwnershipPreview> =>
    apiClient.postLong<OwnershipPreview>(`${baseRevenda}/${instanceId}/transfer/preview`, body),

  transfer: (instanceId: string, body: ResellerTransferRequest): Promise<OwnershipResult> =>
    apiClient.postLong<OwnershipResult>(`${baseRevenda}/${instanceId}/transfer`, body),
}
